/**
 * Whether a receiver can afford a package, for the Finalize Trade modal's
 * funding block. Pure arithmetic on two server-provided integers (the package
 * total and the receiver's balance): nothing here prices anything.
 *
 * It mirrors the server's rule at initiation: a package is rejected when it
 * has value and the receiver's balance is below it (`total > 0 && balance <
 * total`), on the RAW balance. Balances can be negative on this platform (a
 * clawed-back bonus), so the balance is never clamped for the arithmetic: a
 * receiver at -500 facing a 100-point package is 600 short, not 100, and
 * sending 100 would not fix it. Only the bar's fill is clamped, for display.
 */

/** Above this shortfall the fix is removing cards, so the "send a few points" hint is hidden. */
export const SEND_POINTS_HELPER_MAX_DEFICIT = 100;

export interface FundingState {
  /** The receiver can take this package: it has no value, or the balance covers it. */
  funded: boolean;
  /** Points the receiver is missing, from the raw balance; 0 when funded. */
  shortBy: number;
  /** The receiver's balance once this trade is paid for (raw, may be negative); 0 when under-funded. */
  balanceAfter: number;
  /**
   * The bar's filled share, clamped to 0..1.
   * Under-funded: the bar is the package, filled to the balance (balance / total);
   * a balance at or below zero fills nothing.
   * Funded: the bar is the balance, filled to the package (total / balance).
   */
  fillRatio: number;
}

const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

export const getFundingState = (totalValue: number, receiverBalance: number): FundingState => {
  const total = Math.max(0, totalValue);
  const balance = receiverBalance;

  if (total > 0 && balance < total) {
    return {
      funded: false,
      shortBy: total - balance,
      balanceAfter: 0,
      fillRatio: clamp01(balance / total),
    };
  }

  return {
    funded: true,
    shortBy: 0,
    balanceAfter: balance - total,
    // A zero-value package fills nothing, whatever the balance.
    fillRatio: balance > 0 ? clamp01(total / balance) : 0,
  };
};
